import React from 'react';
import { PaperClipIcon } from '@heroicons/react/24/outline';
import tw from 'twin.macro';
import Tooltip2 from '@teetsh/app/src/components/Tooltip2';
import { PreviewImg } from '@teetsh/app/src/components/Attachments/PreviewImg';
import { isURLAnImage } from '@teetsh/app/src/components/Attachments/isUrlImage';
import { Attachment } from '@teetsh/app/src/types/attachment';
import { getSeanceAttachmentUrl } from '@teetsh/app/src/utils/urls';
import { useTranslation } from 'react-i18next';
import { printSize } from 'utils/printSize';
import { getFileIcon } from './getFileIcon';

interface Props {
  className?: string;
  showTitle?: boolean;
  showFileSize?: boolean;
  preview?: boolean;
}

function NewAttachmentLink(
  props: Props & {
    attachment: Attachment;
  },
) {
  const { attachment, preview, showFileSize = true } = props;
  const { t } = useTranslation(['common']);
  const downloadUrl = getSeanceAttachmentUrl(
    attachment.s3Path,
    attachment.s3Filename,
    attachment.label,
  );

  const FileIcon = getFileIcon(downloadUrl);

  const Component = (
    <div tw="inline-flex items-center bg-neutral-supershy rounded-lg overflow-hidden">
      <div tw="flex flex-row gap-2.5 items-center px-3 py-2">
        <FileIcon tw="h-5 w-5 shrink-0 text-neutral-moderate" />
        <div tw="flex flex-col">
          <a
            onClick={e => e.stopPropagation()}
            href={downloadUrl}
            rel="noreferrer noopener"
            css={[
              tw`text-sm font-semibold text-brand-moderate hover:underline`,
            ]}
            target="_blank"
          >
            {attachment.label}
          </a>
          {attachment.fileSize && showFileSize && (
            <span tw="text-xs text-neutral-quiet">
              {printSize(attachment.fileSize, t('common:fileSizeLocale'))}
            </span>
          )}
        </div>
      </div>
    </div>
  );
  if (isURLAnImage(downloadUrl) && preview) {
    return (
      <Tooltip2
        label={
          <PreviewImg
            alt={attachment.label}
            fileUrl={downloadUrl}
            maxHeight="250px"
            maxWidth="250px"
          />
        }
      >
        {Component}
      </Tooltip2>
    );
  }
  return Component;
}

export function NewAttachmentLinks(
  props: Props & {
    attachments: Attachment[];
    showNbOfAttachments?: boolean;
  },
) {
  const { t } = useTranslation(['common']);
  if ((props.attachments || []).length === 0) {
    return null;
  }
  const { attachments, className, showTitle = true, ...rest } = props;
  return (
    <div className={className} tw="flex flex-col gap-2">
      {showTitle && (
        <div tw="flex items-center gap-1">
          <PaperClipIcon tw="h-4 w-4 text-neutral-moderate shrink-0" />
          <span tw="text-sm font-semibold text-neutral-moderate">
            {t('common:attachment', { count: attachments.length })}
          </span>
        </div>
      )}
      <div tw="flex flex-row gap-2.5 flex-wrap">
        {attachments.map((attachment: Attachment) => (
          <NewAttachmentLink
            key={attachment.s3Filename}
            attachment={attachment}
            {...rest}
          />
        ))}
      </div>
    </div>
  );
}
